/**
 * FeatureLockedOffer Component
 *
 * The web locked-with-upgrade card. Rendered only where COMMERCE_ENABLED — see FeatureLocked.tsx.
 * Displays a locked overlay with upgrade prompt when a feature is unavailable.
 * Can be used standalone or as fallback for TierGate.
 */

import React from 'react';
import { View, Text, Pressable, StyleSheet } from 'react-native';
import { BlurView } from 'expo-blur';
import { LinearGradient } from 'expo-linear-gradient';
import { Lock, Sparkles, ChevronRight } from 'lucide-react-native';
import Animated, { FadeIn, FadeInUp } from 'react-native-reanimated';
import * as Haptics from 'expo-haptics';
import { useRouter } from 'expo-router';

import AnimatedPressable from '@/components/AnimatedPressable';
import { useThemeColors } from '@/lib/useThemeColors';
import { useSubscriptionStore } from '@/lib/state/subscription-store';
import type { StartupFeatureKey, InvestorFeatureKey } from '@/lib/subscription/features';
import { FEATURE_INFO, getMinimumStartupTier, getMinimumInvestorTier } from '@/lib/subscription/features';
import type { StartupTier, InvestorTier } from '@/lib/subscription/types';
import { STARTUP_TIERS, INVESTOR_TIERS } from '@/lib/subscription/types';

// ============================================================================
// Types
// ============================================================================

export interface FeatureLockedProps {
  feature: StartupFeatureKey | InvestorFeatureKey;
  title?: string;
  description?: string;
  variant?: 'overlay' | 'card' | 'inline' | 'minimal';
  showBlur?: boolean;
  children?: React.ReactNode;
}

// ============================================================================
// Component
// ============================================================================

export function FeatureLockedOffer({
  feature,
  title,
  description,
  variant = 'card',
  showBlur = true,
  children,
}: FeatureLockedProps) {
  const colors = useThemeColors();
  const router = useRouter();
  const userRole = useSubscriptionStore((s) => s.userRole);
  const currentTier = useSubscriptionStore((s) => s.getCurrentTier());

  // Get feature info
  const featureInfo = FEATURE_INFO[feature];
  const displayTitle = title || featureInfo?.name || 'Premium Feature';
  const displayDescription = description || featureInfo?.description || 'Upgrade to unlock this feature';

  // Get minimum required tier
  const requiredTier =
    userRole === 'startup'
      ? getMinimumStartupTier(feature as StartupFeatureKey)
      : getMinimumInvestorTier(feature as InvestorFeatureKey);

  // Get tier config for display
  const tierConfig =
    userRole === 'startup'
      ? STARTUP_TIERS[requiredTier as StartupTier]
      : INVESTOR_TIERS[requiredTier as InvestorTier];

  const handleUpgrade = () => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
    if (userRole === 'startup') {
      router.push('/(startup)/startup-subscription');
    } else {
      router.push('/(investor)/investor-subscription');
    }
  };

  // Minimal variant - just a small lock icon with text
  if (variant === 'minimal') {
    return (
      <Pressable onPress={handleUpgrade} style={styles.minimalContainer}>
        <Lock size={14} color={colors.text.tertiary} />
        <Text style={[styles.minimalText, { color: colors.text.tertiary }]}>
          {tierConfig?.name}+
        </Text>
      </Pressable>
    );
  }

  // Inline variant - compact horizontal bar
  if (variant === 'inline') {
    return (
      <Animated.View entering={FadeIn.duration(300)}>
        <AnimatedPressable onPress={handleUpgrade}>
          <View
            style={[
              styles.inlineContainer,
              {
                backgroundColor: `${colors.primary[500]}10`,
                borderColor: colors.primary[200],
              },
            ]}
          >
            <View style={[styles.inlineIconBg, { backgroundColor: colors.primary[100] }]}>
              <Lock size={14} color={colors.primary[600]} />
            </View>
            <View style={styles.inlineTextContainer}>
              <Text style={[styles.inlineTitle, { color: colors.text.primary }]}>
                {displayTitle}
              </Text>
              <Text style={[styles.inlineDescription, { color: colors.text.secondary }]}>
                Upgrade to {tierConfig?.name} to unlock
              </Text>
            </View>
            <ChevronRight size={18} color={colors.primary[500]} />
          </View>
        </AnimatedPressable>
      </Animated.View>
    );
  }

  // Overlay variant - blurred overlay over children
  if (variant === 'overlay' && children) {
    return (
      <View style={styles.overlayWrapper}>
        {/* Children shown behind blur */}
        <View style={styles.overlayChildren}>{children}</View>

        {/* Blur overlay */}
        {showBlur ? (
          <BlurView
            intensity={20}
            tint="light"
            style={StyleSheet.absoluteFill}
          />
        ) : (
          <View
            style={[StyleSheet.absoluteFill, { backgroundColor: 'rgba(255,255,255,0.85)' }]}
          />
        )}

        {/* Lock content */}
        <View style={styles.overlayContent}>
          <Animated.View entering={FadeInUp.delay(100).duration(400)}>
            <View
              style={[
                styles.overlayLockBg,
                { backgroundColor: colors.primary[50] },
              ]}
            >
              <Lock size={28} color={colors.primary[600]} />
            </View>
          </Animated.View>

          <Animated.View entering={FadeInUp.delay(200).duration(400)}>
            <Text style={[styles.overlayTitle, { color: colors.text.primary }]}>
              {displayTitle}
            </Text>
            <Text style={[styles.overlayDescription, { color: colors.text.secondary }]}>
              {displayDescription}
            </Text>
          </Animated.View>

          <Animated.View entering={FadeInUp.delay(300).duration(400)}>
            <Pressable
              onPress={handleUpgrade}
              style={({ pressed }) => ({ opacity: pressed ? 0.9 : 1 })}
            >
              <LinearGradient
                colors={[colors.primary[500], colors.primary[600]]}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 0 }}
                style={styles.overlayButton}
              >
                <Sparkles size={16} color="#fff" />
                <Text style={styles.overlayButtonText}>
                  Upgrade to {tierConfig?.name}
                </Text>
              </LinearGradient>
            </Pressable>
          </Animated.View>
        </View>
      </View>
    );
  }

  // Card variant (default) - standalone card
  return (
    <Animated.View entering={FadeIn.duration(300)}>
      <View
        style={[
          styles.cardContainer,
          {
            backgroundColor: colors.surface.primary,
            borderColor: colors.border.light,
          },
        ]}
      >
        {/* Gradient accent */}
        <LinearGradient
          colors={[colors.primary[400], colors.primary[600]]}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 0 }}
          style={styles.cardAccent}
        />

        {/* Lock icon */}
        <View
          style={[
            styles.cardLockBg,
            { backgroundColor: colors.primary[50] },
          ]}
        >
          <Lock size={24} color={colors.primary[600]} />
        </View>

        {/* Content */}
        <Text style={[styles.cardTitle, { color: colors.text.primary }]}>
          {displayTitle}
        </Text>
        <Text style={[styles.cardDescription, { color: colors.text.secondary }]}>
          {displayDescription}
        </Text>

        {/* Upgrade button */}
        <AnimatedPressable
          onPress={handleUpgrade}
          style={[
            styles.cardButton,
            {
              backgroundColor: colors.primary[500],
            },
          ]}
        >
          <Sparkles size={16} color="#fff" />
          <Text style={styles.cardButtonText}>
            Upgrade to {tierConfig?.name}
          </Text>
        </AnimatedPressable>

        {/* Tier badge */}
        <View
          style={[
            styles.tierBadge,
            { backgroundColor: colors.primary[100] },
          ]}
        >
          <Text style={[styles.tierBadgeText, { color: colors.primary[700] }]}>
            {tierConfig?.name}+ Feature
          </Text>
        </View>
      </View>
    </Animated.View>
  );
}

// ============================================================================
// Styles
// ============================================================================

const styles = StyleSheet.create({
  // Minimal variant
  minimalContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  minimalText: {
    fontSize: 12,
    fontWeight: '500',
  },

  // Inline variant
  inlineContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderRadius: 12,
    borderWidth: 1,
    gap: 12,
  },
  inlineIconBg: {
    width: 36,
    height: 36,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
  },
  inlineTextContainer: {
    flex: 1,
  },
  inlineTitle: {
    fontSize: 14,
    fontWeight: '600',
  },
  inlineDescription: {
    fontSize: 12,
    marginTop: 2,
  },

  // Overlay variant
  overlayWrapper: {
    position: 'relative',
    overflow: 'hidden',
    borderRadius: 16,
  },
  overlayChildren: {
    opacity: 0.4,
  },
  overlayContent: {
    ...StyleSheet.absoluteFillObject,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  overlayLockBg: {
    width: 64,
    height: 64,
    borderRadius: 32,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 16,
  },
  overlayTitle: {
    fontSize: 18,
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: 8,
  },
  overlayDescription: {
    fontSize: 14,
    textAlign: 'center',
    lineHeight: 20,
    marginBottom: 20,
  },
  overlayButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 12,
    gap: 8,
  },
  overlayButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },

  // Card variant
  cardContainer: {
    borderRadius: 20,
    padding: 24,
    borderWidth: 1,
    alignItems: 'center',
    overflow: 'hidden',
  },
  cardAccent: {
    position: 'absolute',
    top: 0,
    left: 24,
    right: 24,
    height: 3,
    borderRadius: 2,
  },
  cardLockBg: {
    width: 56,
    height: 56,
    borderRadius: 28,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 16,
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: 8,
  },
  cardDescription: {
    fontSize: 14,
    textAlign: 'center',
    lineHeight: 20,
    marginBottom: 20,
    paddingHorizontal: 16,
  },
  cardButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 24,
    paddingVertical: 14,
    borderRadius: 14,
    gap: 8,
  },
  cardButtonText: {
    color: '#fff',
    fontSize: 15,
    fontWeight: '600',
  },
  tierBadge: {
    position: 'absolute',
    top: 16,
    right: 16,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 8,
  },
  tierBadgeText: {
    fontSize: 11,
    fontWeight: '600',
  },
});
